/** Thema icon set — minimal stroke-based line icons, ported from the design. */

import type { CSSProperties, ReactElement, ReactNode } from "react";

type IconProps = {
  size?: number;
  sw?: number;
  fill?: string;
  style?: CSSProperties;
  className?: string;
};

function Icon({
  d,
  size = 20,
  sw = 1.6,
  fill = "none",
  children,
  style,
  className,
}: IconProps & { d?: string; children?: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      aria-hidden="true"
    >
      {d ? <path d={d} /> : children}
    </svg>
  );
}

export const Icons: Record<string, (p: IconProps) => ReactElement> = {
  home: (p) => <Icon {...p} d="M3 10.5 12 4l9 6.5M5 9.5V20h14V9.5" />,
  chart: (p) => (
    <Icon {...p}>
      <path d="M4 19V5M4 19h16" />
      <path d="M8 16l3.5-4 3 2.5L20 8" />
    </Icon>
  ),
  layers: (p) => (
    <Icon {...p}>
      <path d="M12 3 3 8l9 5 9-5-9-5Z" />
      <path d="M3 13l9 5 9-5M3 18l9 5 9-5" />
    </Icon>
  ),
  vault: (p) => (
    <Icon {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="12" cy="12" r="4" />
      <path d="M12 8v1M12 15v1M8 12h1M15 12h1" />
    </Icon>
  ),
  plus: (p) => <Icon {...p} d="M12 5v14M5 12h14" />,
  shield: (p) => (
    <Icon {...p}>
      <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
      <path d="M9 12l2 2 4-4" />
    </Icon>
  ),
  doc: (p) => (
    <Icon {...p}>
      <path d="M7 3h7l5 5v13H7z" />
      <path d="M14 3v5h5M9.5 13h5M9.5 16.5h5" />
    </Icon>
  ),
  gear: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1 7 17M17 7l2.1-2.1" />
    </Icon>
  ),
  wallet: (p) => (
    <Icon {...p}>
      <rect x="3" y="6" width="18" height="13" rx="2.5" />
      <path d="M3 10h18" />
      <circle cx="16.5" cy="14" r="1.1" fill="currentColor" stroke="none" />
    </Icon>
  ),
  bell: (p) => (
    <Icon {...p}>
      <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </Icon>
  ),
  search: (p) => (
    <Icon {...p}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-3.5-3.5" />
    </Icon>
  ),
  arrow: (p) => <Icon {...p} d="M5 12h14M13 6l6 6-6 6" />,
  arrowUp: (p) => <Icon {...p} d="M12 19V5M6 11l6-6 6 6" />,
  arrowDn: (p) => <Icon {...p} d="M12 5v14M6 13l6 6 6-6" />,
  check: (p) => <Icon {...p} d="M5 12.5l4.5 4.5L19 7" />,
  close: (p) => <Icon {...p} d="M6 6l12 12M18 6 6 18" />,
  copy: (p) => (
    <Icon {...p}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </Icon>
  ),
  ext: (p) => (
    <Icon {...p}>
      <path d="M14 5h5v5M19 5l-8 8" />
      <path d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </Icon>
  ),
  logout: (p) => (
    <Icon {...p}>
      <path d="M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4" />
      <path d="M10 12H3M6 8l-4 4 4 4" />
    </Icon>
  ),
  chevron: (p) => <Icon {...p} d="M9 6l6 6-6 6" />,
  chevDn: (p) => <Icon {...p} d="M6 9l6 6 6-6" />,
  spark: (p) => (
    <Icon {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
      <path d="M12 8.5a3.5 3.5 0 0 0 0 7 3.5 3.5 0 0 0 0-7Z" />
    </Icon>
  ),
  pulse: (p) => <Icon {...p} d="M3 12h4l2-6 4 12 2-6h6" />,
  globe: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
    </Icon>
  ),
  lock: (p) => (
    <Icon {...p}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Icon>
  ),
};

/** Google "G" multi-color glyph for the sign-in button. */
export function GoogleG({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.66-.06-1.3-.17-1.9H12v3.6h5.39a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.89-1.74 2.98-4.3 2.98-7.22Z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.23-2.5c-.9.6-2.04.95-3.39.95-2.6 0-4.8-1.76-5.59-4.12H3.07v2.59A10 10 0 0 0 12 22Z"
      />
      <path
        fill="#FBBC05"
        d="M6.41 13.9a6 6 0 0 1 0-3.8V7.51H3.07a10 10 0 0 0 0 8.98l3.34-2.59Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.98c1.47 0 2.78.5 3.82 1.5l2.85-2.85C16.96 2.99 14.7 2 12 2A10 10 0 0 0 3.07 7.51l3.34 2.59C7.2 7.74 9.4 5.98 12 5.98Z"
      />
    </svg>
  );
}
